import React, { useCallback, useState, useEffect } from "react"
import { useFormContext } from "react-hook-form"

import {
  Box,
  Link,
  makeStyles,
  Theme,
  OutlinedInput,
  Button,
} from "@material-ui/core"
import { InputLabel } from "../@citydna/common/InputLabel"

import { RenderUploadedImage } from "./RenderUploadedImage"
import { SchemaField } from "../featuer-layer-settings/schema"

import clsx from "clsx"
import { DragDropArea } from "../common/DragDropArea"

interface ImageUploadFieldProps {
  field: SchemaField
}

/** Scoped @material-ui/core stylesheet */
const useStyles = makeStyles<Theme>(theme => ({
  input: {
    display: "none",
  },
  imagePreview: {
    display: "flex",
  },
  previewImage: {
    borderRadius: theme.shape.borderRadius,
    marginRight: theme.spacing(2),
  },
  link: {
    fontSize: 12,
    fontWeight: "normal",
    color: theme.palette.secondary.main,
    height: 28,
    "&:hover": {
      textDecoration: "none",
    },
  },
  removeLink: {
    color: theme.palette.secondary.main,
  },
}))

/**
 * TODO: Add remove functionality
 */

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  field,
}) => {
  /** Hook up the the form */
  const { setValue, register, unregister, watch } = useFormContext()
  const value = watch(field.name || "")
  useEffect(() => {
    if (field.name) {
      register(field.name)
      // @ts-ignore
      return () => unregister(field.name)
    }
  }, [register, unregister, field.name])

  /** Memoise onDrop callback so we don't cause multiple re-renders */
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (field.name) {
        setValue(field.name, acceptedFiles[0])
      }
    },
    [field.name, setValue]
  )

  /** Handle removing the image */
  const handleRemoveImage = (e: any) => {
    if (field.name) {
      setValue(field.name, "")
    }
  }

  /** Toggle between upload and enter URL mode */
  const [mode, setMode] = useState<"upload" | "url">("upload")
  const handleChangeMode = (e: any) => {
    e.preventDefault()
    handleRemoveImage(() => (mode === "upload" ? "" : undefined))
    setMode(m => (m === "upload" ? "url" : "upload"))
  }

  /** Get scoped CSS classes */
  const { errors } = useFormContext()
  const error = errors[field.name || ""]
  const classes = useStyles()

  return (
    <Box>
      <Box display="flex">
        <InputLabel htmlFor={field.name}>
          {field.alias}
          {mode === "url" ? " uri" : ""}
          {!field.required && " (optional)"}
          {mode === "upload" && value && (
            <Link
              component={Button}
              size="small"
              onClick={handleRemoveImage}
              className={clsx(classes.link, classes.removeLink)}
            >
              Change
            </Link>
          )}
        </InputLabel>
        <Box ml="auto">
          <Link
            color="secondary"
            component={Button}
            size="small"
            onClick={handleChangeMode}
            className={classes.link}
          >
            {mode === "upload" ? "Enter image URI directly" : "Upload file"}
          </Link>
        </Box>
      </Box>
      {mode === "upload" ? (
        value ? (
          <>
            <RenderUploadedImage value={value} />
          </>
        ) : (
          <DragDropArea
            multiple={false}
            onDrop={onDrop}
            dragActiveText="Drop the image"
            dragInactiveText="Drag and drop and image"
            error={error}
            accept={["image/png", "image/jpeg"]}
          />
        )
      ) : (
        <OutlinedInput
          color="secondary"
          name={field.name}
          id={field.name}
          fullWidth
          inputRef={register({ required: field.required, max: field.length })}
        />
      )}
    </Box>
  )
}
